import {
  createContext,
  useContext,
  PropsWithChildren,
  useState,
  useEffect
} from 'react';
import { useRouter } from 'next/router';

import useTickets from '../hooks/useTickets';
import {
  getDiscountCodeByCode,
  useGetSuggestedFriendsFromEvent,
  useValidateEventUrlCommand
} from '../../../../hooks';
import { useGetMe } from 'legacy/user/hooks';
import {
  MultiTierCheckoutProps,
  MultiTierCheckoutProviderT,
  QuantityChangeEvents
} from '../types';
import useMultiTiers from '../hooks/useMultiTiers';
import useMultiTierCheckoutSession from '../hooks/useMultiTierCheckoutSession';
import {
  CheckoutUpdateDataToSend,
  MultiTierCheckoutSession
} from '../../../../types';
import useSpecialCode from '../hooks/useSpecialCode';
import usePaymentMethod from 'hooks/usePaymentMethod';
import useWaivers from '../hooks/useWaivers';
import { LoginResponse } from 'legacy/auth/types';
import useRequestScreening from '../hooks/useRequestScreening';
import { Card } from 'legacy/common/types/models';

// NOTE OLD CODE / HACK
// This seems to be always be enough time, in the off chance it is not then the user will be prompted to restart the checkout session.
const WAIT_TIME_FOR_NEWLY_AUTHENTICATED_USER_MILLISECONDS = 2000;

const MultiTierCheckoutContext = createContext(
  {} as MultiTierCheckoutProviderT
);

/**
 * Provider handling the multi-tier checkout functionality.
 *
 * This is the main conductor of the checkout flow.
 * It uses the different hooks that are responsible for each domain of the checkout.
 * It also implements the  update code responsible to update the checkout session after a given action is taken.
 * Finally, it takes care of the "on mount" logic setting the checkout values based on the passed props from the event.
 */
function MultiTierCheckoutProvider({
  children,
  onClose,
  event,
  unlockedTier,
  defaultDiscountCode,
  defaultPromoterCode,
  onSuccess,
  onViewEventPostCheckout,
  metaPixelRef
}: PropsWithChildren<MultiTierCheckoutProps>) {
  const [eventShareToken, setEventShareToken] = useState<string | null>(null);
  const [isAuthenticatingUser, setIsAuthenticatingUser] = useState(false);

  // Default discount code is applied inside a useEffect below as it is dependent on checkout session update mutation.
  const specialCode = useSpecialCode();
  const discountData = getDiscountCodeByCode(
    event._id,
    specialCode.discountCode.value
  );

  const validateEventUrlCommand = useValidateEventUrlCommand();
  const router = useRouter();
  const { data: user } = useGetMe();
  const checkoutSession = useMultiTierCheckoutSession(event, discountData.data);
  const tickets = useTickets(event);
  const ticketTiers = useMultiTiers(event);
  const paymentMethod = usePaymentMethod({
    returnUrl: window.location.href
  });
  const waivers = useWaivers(event);
  const requestScreening = useRequestScreening(
    event._id,
    tickets.tierRequiringApproval,
    ticketTiers.data
  );

  // Prefetch Suggested Friends during checkout
  useGetSuggestedFriendsFromEvent(event._id);

  const isLoading = Boolean(
    checkoutSession.isStartingCheckout ||
      checkoutSession.isUpdatingCheckout ||
      ticketTiers.isLoadingGetTierFromShareToken ||
      isAuthenticatingUser
  );

  /**
   * Gathers checkout update data.
   * This allows to retrieve the data from the different hooks to be sent to the API.
   * This also allows us to easily call the checkout update and only update the actual state on success.
   */
  const checkoutSessionUpdater = (
    updateData: Partial<CheckoutUpdateDataToSend>,
    cards?: Card[]
  ): ReturnType<typeof checkoutSession.updateCheckout> => {
    const checkoutUpdateData: CheckoutUpdateDataToSend = {
      card:
        updateData.card ||
        paymentMethod.selectedCard.value ||
        (checkoutSession.checkoutSession as MultiTierCheckoutSession).card,
      promoterCode: updateData.discountCode || discountData?.data?.code,
      discountCode:
        updateData.discountCodeType === 'discountCode' ||
        (discountData?.data?.discount &&
          updateData.discountCodeType !== 'promoterAttribution')
          ? updateData.discountCode || discountData?.data.code
          : undefined,
      ticketTiers: updateData.ticketTiers || tickets.selectedTicketTiers,
      privateTierCodes:
        updateData.privateTierCodes || ticketTiers.privateTierCodes,
      stripePaymentMethodType: updateData.stripePaymentMethodType || undefined
    };

    return checkoutSession.updateCheckout(checkoutUpdateData, cards);
  };

  /**
   * Handles the success of authentication for signed otu users trying to make a transaction.
   * Hack for simple sign on to wait for any resources on the newly authenticated user's Stripe customer account to be created,
   * and then to update the active checkout session with the user.
   */
  const onAuthenticationSuccess = (loginResponse: LoginResponse) => {
    setIsAuthenticatingUser(true);

    if (eventShareToken) {
      validateEventUrlCommand.mutate({
        eventId: event._id,
        shareToken: eventShareToken
      });
    }

    if (!tickets.tierRequiringApproval) {
      setTimeout(() => {
        if (loginResponse.user.defaultCard) {
          paymentMethod.selectedCard.setSelectedCard(
            loginResponse.user.defaultCard
          );
        }

        checkoutSessionUpdater(
          {
            card: loginResponse.user.defaultCard ?? null
          },
          loginResponse.user.cards
        )
          .then(() => {
            setIsAuthenticatingUser(false);
          })
          .catch(() => {
            setIsAuthenticatingUser(false);
          });
      }, WAIT_TIME_FOR_NEWLY_AUTHENTICATED_USER_MILLISECONDS);
    } else {
      setIsAuthenticatingUser(false);
      onSuccess();
    }
  };

  /**
   * Applies a discount code and updates the checkout session.
   */
  const applyDiscountCodeAndSave = (
    code: string,
    discountCodeType?: string
  ) => {
    specialCode.discountCode.applyDiscountCode(code, (discountCode) =>
      checkoutSessionUpdater({ discountCode, discountCodeType })
    );
  };

  /**
   * Toggles the ticket tier and updates the checkout session.
   */
  const toggleTicketTierAndSave = (
    tierId: string,
    unlockCode?: string,
    discountCode?: string
  ) => {
    tickets.toggleTicketTier(tierId, (newTickets) => {
      const updateData = {
        ticketTiers: newTickets,
        privateTierCodes: null,
        discountCode
      };

      // When unlocking a private tier via a code, we need to pass all unlock codes the user has entered to the API.
      // This allows us to support unlocking multiple private tiers.
      // But at this stage, the private tier codes state has not necessarily yet been updated to include the new unlock code,
      // so we are adding it manually here in order to send the optimistic update to the API.
      if (unlockCode) {
        updateData.privateTierCodes = {
          ...ticketTiers.privateTierCodes,
          [tierId]: unlockCode
        };
      }

      return checkoutSessionUpdater(updateData);
    });
  };

  /**
   * Updates the quantity of a ticket tier and updates the checkout session.
   */
  const updateTicketTierQuantityAndSave = (
    ticketId: string,
    event: QuantityChangeEvents
  ) => {
    tickets.updateQuantity(ticketId, event, (newTickets) =>
      checkoutSessionUpdater({ ticketTiers: newTickets })
    );
  };

  /**
   * Adds a new card to the user's account and updates the checkout session.
   */
  const addNewCardAndSave = (options?: { onSuccess: () => void }) =>
    paymentMethod.addNewCard({
      onSuccess: (newCard: string, cards: Card[]) =>
        checkoutSessionUpdater({ card: newCard }, cards).finally(() => {
          options?.onSuccess();
        })
    });

  /**
   * Sets a card to be used for the checkout session and updates the checkout session.
   */
  const selectCardAndSave = (
    selectedCardId: string,
    options?: { onSuccess: () => void }
  ) =>
    paymentMethod.selectedCard.setSelectedCard(selectedCardId, (cardId) =>
      checkoutSessionUpdater({ card: cardId }).finally(() => {
        options?.onSuccess();
      })
    );

  /**
   * Sets a card to be used for the checkout session and updates the checkout session.
   */
  const selectBuyNowPayLaterAndSave = (
    selectedBuyNowPayLater: 'klarna' | 'affirm' | null,
    options?: { onSuccess: () => void }
  ) =>
    paymentMethod.selectedBuyNowPayLater.setSelectedBuyNowPayLater(
      selectedBuyNowPayLater,
      (buyNowPayLater) =>
        checkoutSessionUpdater({
          stripePaymentMethodType: buyNowPayLater
        }).finally(() => {
          options?.onSuccess();
        })
    );

  /**
   * Executes when a checkout session is successfully created.
   * This function applies an initial discount code, credit card and sets the unlocked tier as the initially selected tier (if tier exists on the event).
   */
  useEffect(() => {
    if (
      checkoutSession.checkoutSession &&
      !checkoutSession.isStartingCheckout
    ) {
      if (checkoutSession.isNewCheckoutEnabled && user?.defaultCard) {
        paymentMethod.selectedCard.setSelectedCard(user.defaultCard);
      } else if (
        !checkoutSession.isNewCheckoutEnabled &&
        (checkoutSession.checkoutSession as MultiTierCheckoutSession).card
      ) {
        paymentMethod.selectedCard.setSelectedCard(
          (checkoutSession.checkoutSession as MultiTierCheckoutSession).card
        );
      }

      if ((defaultDiscountCode || defaultPromoterCode) && !unlockedTier) {
        specialCode.discountCode.applyDiscountCode(
          defaultDiscountCode || defaultPromoterCode
        );
      } else if (unlockedTier) {
        const tierToBeUnlocked = event?.pricing?.tiers?.find(
          (tier) => tier._id === unlockedTier.id
        );

        // Unlocked tier codes from outside the checkout flow have been checked
        // But the tier itself has not yet been checked, we need to make sure we can safely add it to the checkout session.
        // We are also ignoring the discount code here, as we can't have discount codes for private tiers.
        if (tierToBeUnlocked && !tierToBeUnlocked.isDisabled) {
          ticketTiers.setPrivateTierCode(
            unlockedTier.id,
            unlockedTier.shareToken
          );

          if (!ticketTiers.isTierDisabled(unlockedTier.id)) {
            if (ticketTiers.isTierRequiringApproval(unlockedTier.id)) {
              tickets.toggleTierRequiringApproval(unlockedTier.id);
            } else {
              toggleTicketTierAndSave(unlockedTier.id, unlockedTier.shareToken);
            }
          }
        }
      }
    }
  }, [checkoutSession.isStartingCheckout]);

  /**
   * Starts a checkout session
   */
  useEffect(() => {
    if (!event?._id) {
      return;
    }

    checkoutSession.startCheckout(event._id);

    // Track checkout start event with Meta Pixel
    if (metaPixelRef?.current) {
      metaPixelRef.current.trackCheckoutInitiated({
        eventId: event._id,
        eventName: event.title
      });
    }
  }, [event?._id]);

  // Not a huge fan of this implementation.
  // When a user selects a tier for approval while logged out, we need to make sure they still have access to that tier after they logged in.
  useEffect(() => {
    if (tickets.tierRequiringApproval && user) {
      if (ticketTiers.isTierDisabled(tickets.tierRequiringApproval)) {
        tickets.toggleTierRequiringApproval(tickets.tierRequiringApproval);
      }
    }
  }, [user, ticketTiers.data]);

  /**
   * NOTE: VERY OLD CODE.
   * TODO: Find if this is actually useful or patching a bug somewhere.
   *
   * This ensures that checkout sessions which begin with an unauthenticated user for a private event do
   * not have the checkout modal dismissed following authentication. Without this the rendering logic for
   * the event screen will render an entirely different component instead (`EventInvite`), thus clearing
   * the active checkout session for the user.
   */
  useEffect(() => {
    const { invite } = router.query;

    if (invite && !user) {
      setEventShareToken(invite as string);
      delete router.query.invite;
      router.push(router);
    }
  }, []);

  /**
   * Wraps the onSuccess with Meta Pixel tracking
   */
  const handleCheckoutSuccess = () => {
    if (metaPixelRef?.current) {
      metaPixelRef.current.trackCheckoutSuccess({
        eventId: event._id,
        eventName: event.title,
        value: checkoutSession.checkoutSession?.total || 0
      });
    }

    onSuccess();
  };

  return (
    <MultiTierCheckoutContext.Provider
      value={{
        data: {
          checkoutSession,
          appliedDiscount: {
            data: discountData.data,
            isLoading: discountData.isLoading
          },
          event,
          isLoading,
          paymentMethod,
          requestScreening,
          specialCode,
          tickets,
          ticketTiers,
          user,
          waivers
        },
        onClose,
        onSuccess: handleCheckoutSuccess,
        onViewEventPostCheckout,
        toggleTicketTierAndSave,
        updateTicketTierQuantityAndSave,
        applyDiscountCodeAndSave,
        addNewCardAndSave,
        selectCardAndSave,
        selectBuyNowPayLaterAndSave,
        onAuthenticationSuccess
      }}
    >
      {children}
    </MultiTierCheckoutContext.Provider>
  );
}

export const useMultiTierCheckout = () => useContext(MultiTierCheckoutContext);

export default MultiTierCheckoutProvider;
